import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import useReveal from "../lib/useReveal";
import useSeo from "../lib/useSeo";
import useJsonLd from "../lib/useJsonLd";
import Marquee from "../components/Marquee";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG = "/hero-mumbai.jpg";
const FOUNDATION_IMG = "/foundation-craft.jpg";

const STORIES = [
  {
    img: "/deepali.jpg",
    aspect: "522 / 841",
    kicker: "Human · Bengal",
    title: "Deepali Ghosh, 70 — and the rice shop that refused to close.",
    views: "50M+ views",
    watch: [
      {
        label: "Full Documentary",
        url: "https://www.youtube.com/watch?v=2xjNkZI8pcI&t=1s",
        platform: "YouTube",
      },
      {
        label: "Part 1",
        url: "https://www.instagram.com/storiesbyaradhana/reel/DUiRFBPD3YJ/",
        platform: "Instagram",
      },
      {
        label: "Part 2",
        url: "https://www.instagram.com/p/DU-plhDj1qE/",
        platform: "Instagram",
      },
    ],
    features: [
      {
        name: "India Today",
        url: "https://www.indiatoday.in/trending-news/story/bengal-woman-70-battles-hardship-alone-to-run-crumbling-eatery-for-30-years-2866398-2026-02-11",
      },
      {
        name: "Indian Express",
        url: "https://indianexpress.com/article/trending/trending-in-india/70-year-old-woman-howrah-roadside-rice-hotel-aradhana-chatterjee-viral-video-10528833/",
      },
      {
        name: "Economic Times",
        url: "https://economictimes.indiatimes.com/news/new-updates/this-70-year-old-is-doing-everything-to-save-her-late-husbands-tiny-rice-shop-viral-story-leaves-netizens-impressed/articleshow/128239028.cms?from=mdr",
      },
      {
        name: "NDTV",
        url: "https://www.ndtv.com/food/watch-viral-video-of-elderly-woman-fighting-to-keep-her-rice-shop-alive-moves-internet-10987238",
      },
      {
        name: "News18",
        url: "https://www.news18.com/viral/in-a-small-market-corner-bengal-womans-strength-is-inspiring-thousands-aa-9898991.html",
      },
    ],
    link: "https://www.youtube.com/watch?v=2xjNkZI8pcI&t=1s",
  },
  {
    img: "/jinali.jpg",
    aspect: "520 / 929",
    kicker: "Brand · Sustainability",
    title:
      "Jinali Mody — turning banana waste into vegan leather.",
    views: "30M+ views",
    watch: [
      {
        label: "Reel",
        url: "https://www.instagram.com/reels/C1tNNstLNmB/",
        platform: "Instagram",
      },
    ],
    features: [
      {
        name: "UNEP",
        url: "https://www.unep.org/youngchampions/news/video/jinali-mody-crafting-leather-leaves",
      },
      {
        name: "The Statesman",
        url: "https://www.thestatesman.com/india/jinali-mody-wins-unep-young-champions-of-the-earth-prize-1503490796.html",
      },
      {
        name: "Inshorts",
        url: "https://inshorts.com/en/news/banofi-turns-banana-waste-into-sustainable-vegan-leather-1742797979132",
      },
    ],
    link: "https://www.instagram.com/reels/C1tNNstLNmB/",
  },
  {
    img: "/tea.jpg",
    aspect: "520 / 839",
    kicker: "Human · Bengal",
    title: "A 100-year-old tea stall that runs on trust.",
    views: "6M+ views",
    watch: [
      {
        label: "Reel",
        url: "https://www.instagram.com/reels/DGLB2JHzwS2/",
        platform: "Instagram",
      },
    ],
    features: [
      {
        name: "Al Jazeera",
        url: "https://www.aljazeera.com/features/2025/4/19/i-always-come-here-the-indian-tea-shop-that-runs-on-trust",
      },
      {
        name: "Indian Express",
        url: "https://indianexpress.com/article/cities/kolkata/tea-with-a-dash-of-trust-inside-a-bengal-shop-where-customers-serve-themselves-10035840/",
      },
      {
        name: "Curly Tales",
        url: "https://curlytales.com/taking-self-service-to-another-level-at-this-100-year-old-tea-stall-in-west-bengal-customers-make-serve-their-own-tea/",
      },
    ],
    link: "https://www.instagram.com/reels/DGLB2JHzwS2/",
  },
];

const BRANDS = [
  { name: "Tata Motors", url: "https://www.instagram.com/p/DPLkoU4Dwt5/" },
  { name: "Tata 1mg", url: "https://www.instagram.com/reels/DG7qyFNzeEz/" },
  { name: "Zomato", url: "https://www.instagram.com/p/DJom2S7TZ5T/" },
  { name: "TenXU", url: "https://www.instagram.com/reels/DPmQNmECOH0/" },
  { name: "Heads Up For Tails", url: "https://www.instagram.com/p/CxZg7jSS6A6/" },
  { name: "The Souled Store", url: "https://www.instagram.com/p/DWypR9yj-tB/" },
  { name: "Catch", url: "https://www.instagram.com/p/DIjG5q6znFq/" },
  { name: "ChatGPT", url: "https://www.instagram.com/reel/DW83fJLj8Ys/" },
  { name: "Doctors’ Choice", url: "https://www.instagram.com/p/DTNdJvnCLGc/" },
  { name: "PC Chandra", url: "https://www.instagram.com/p/DRMtyqSEnOW/" },
  { name: "Avimee", url: "https://www.instagram.com/p/DPggyGrj2VA/" },
  { name: "Satyendra Sattoo", url: "https://www.instagram.com/p/DNP5O_mzX_j/" },
  { name: "Z Studios", url: "https://www.instagram.com/p/DLc4Bs3TJJ4/" },
  { name: "Imagimake", url: "https://www.instagram.com/p/DNXdQqmTs15/" },
  { name: "Amwoodo", url: "https://www.instagram.com/p/DWEPONWE9aK/" },
];

const PRESS = [
  "Hindustan Times",
  "NDTV",
  "The Economic Times",
  "Mint",
  "Times of India",
  "The Indian Express",
  "News18",
  "Navbharat Times",
];

const STATS = [
  { label: "Instagram", value: "500K" },
  { label: "Facebook", value: "250K" },
  { label: "YouTube", value: "250K" },
  { label: "Combined views", value: "120M+", note: "past 30 days" },
  { label: "Engagement", value: "20M+", note: "past 30 days" },
  { label: "States covered", value: "10" },
];

function Section({ id, children, className = "" }) {
  return (
    <section
      id={id}
      className={`max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32 ${className}`}
    >
      {children}
    </section>
  );
}

function Reveal({ children, className = "" }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`sba-reveal ${className}`}>
      {children}
    </div>
  );
}

export default function Landing() {
  useSeo({
    title:
      "Stories by Aradhana — Impact-led storytelling studio from India",
    description:
      "Stories by Aradhana is an impact-led content studio from India. Human stories, brand films for Tata Motors, Zomato, Tata 1mg and more, plus the Foundation for Change.",
  });
  useJsonLd(
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Signature Stories — Stories by Aradhana",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: STORIES.length,
      itemListElement: STORIES.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: s.link || s.watch?.[0]?.url,
        name: s.title,
      })),
    },
    "stories-list"
  );
  return (
    <main data-testid="landing-page" className="bg-[#fdfbf7]">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-6 md:pt-10 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 lg:gap-24 items-start">
          <div className="lg:col-span-7 order-2 lg:order-none">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#1a1a1a]">
                Impact-Led Storytelling Studio · India · estd. 2023
              </p>
            </Reveal>

            <Reveal className="mt-8">
              <h1 className="font-serif text-[clamp(2.6rem,7vw,5.75rem)] leading-[0.95] tracking-tight text-[#1a1a1a]">
                We build stories
                <br />
                <span className="italic">that create</span>
                <br />
                <span className="whitespace-nowrap">
                  <span className="relative inline-block mr-3">
                    <span className="absolute inset-x-0 bottom-1 h-3 bg-[#f9d94b]/80 -z-0" />
                    <span className="relative z-10">real world</span>
                  </span>
                  impact.
                </span>
              </h1>
            </Reveal>

            <Reveal className="mt-10 max-w-xl">
              <p className="font-mono text-base md:text-lg leading-relaxed text-[#1a1a1a]">
                A story is not complete when it’s told —
                <br />
                it’s complete when it creates change.
              </p>
            </Reveal>

            <Reveal className="mt-12 flex flex-wrap items-center gap-6">
              <a
                href="#stories"
                data-testid="hero-cta-stories"
                className="inline-flex items-center gap-3 bg-[#1a1a1a] text-[#fdfbf7] px-8 py-4 text-sm font-sans tracking-[0.15em] uppercase hover:bg-[#2d2d2d] transition-colors"
              >
                See the stories
                <ArrowRight size={16} strokeWidth={1.4} />
              </a>
              <Link
                to="/foundation#donate"
                data-testid="hero-cta-donate"
                className="inline-flex items-center gap-3 bg-[#f9d94b] text-[#1a1a1a] px-8 py-4 text-sm font-sans tracking-[0.15em] uppercase hover:bg-[#fde277] transition-colors"
              >
                Donate
                <ArrowRight size={16} strokeWidth={1.4} />
              </Link>
              <a
                href="#collaborate"
                data-testid="hero-cta-collaborate"
                className="sba-link-arrow font-sans text-sm tracking-[0.15em] uppercase text-[#1a1a1a]"
              >
                Collaborate with us
                <span className="arrow">
                  <ArrowUpRight size={16} strokeWidth={1.4} />
                </span>
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-none">
            <Reveal>
              <div className="relative mt-4 md:mt-[56px]">
                <div className="absolute -top-3 -left-3 lg:-top-6 lg:-left-6 w-14 h-14 lg:w-24 lg:h-24 bg-[#f9d94b] -z-0" />
                <div
                  className="relative z-10 w-full overflow-hidden h-[240px] sm:h-[320px] md:h-[420px]"
                >
                  <img
                    src={HERO_IMG}
                    alt="On the road — Mumbai, India"
                    data-testid="hero-image"
                    width="1200"
                    height="900"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="mt-3 md:mt-4 font-mono text-[11px] tracking-[0.2em] uppercase text-[#4a4a4a]">
                  On the road — Mumbai, India
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BRANDS MARQUEE 1 */}
      <Marquee items={BRANDS.map((b) => b.name)} testId="brands-marquee-top" />

      {/* ABOUT */}
      <section
        id="about"
        className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#1a1a1a]">
                Chapter 01 — The beginning
              </p>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#1a1a1a]">
                Stories that refuse to stay unseen.
              </h2>
              <p className="mt-8 font-serif text-2xl md:text-[28px] italic leading-snug text-[#1a1a1a] border-l-2 border-[#f9d94b] pl-5">
                We believe storytelling isn’t just about views — it’s about
                creating change, building communities and leaving a lasting
                impact. Because the right story doesn’t just reach people, it
                moves them to act.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal>
              <div className="space-y-6 font-sans text-[17px] md:text-[18px] leading-[1.75] text-[#1a1a1a]">
                <p>
                  Stories by Aradhana began with a simple thought — there are
                  countless stories around us that deserve to be{" "}
                  <em className="font-serif italic">seen, heard, and felt.</em>{" "}
                  Stories of resilience, kindness, struggle and quiet strength
                  that often go unnoticed in the noise of everyday life.
                </p>
                <p>
                  What started as a journey to document these moments soon
                  became something more. We didn’t just want to tell stories —
                  we wanted to be a part of them. From helping build homes and
                  schools, providing financial assistance and supporting
                  livelihoods, to working on animal welfare initiatives, our
                  work grew into a space where storytelling meets real,
                  on-ground impact.
                </p>
                <p>
                  Across the length and breadth of India, we continue to seek
                  out stories that matter. Some make you pause. Some make you
                  feel. Some move you to act.
                </p>
                <p>
                  Alongside our human-interest work, we partner with startups
                  and companies — with a special focus on&nbsp;
                  <em className="font-serif italic">
                    sustainable companies
                  </em>
                  &nbsp;— to bring their stories to the front, told with the
                  same honesty we bring to every other voice.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* IMPACT STATS */}
      <section className="bg-[#1a1a1a] text-[#fdfbf7]">
        <div
          data-testid="impact-stats"
          className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-12 md:gap-x-6 md:gap-y-16"
        >
          {STATS.map((s, i) => (
            <Reveal key={s.label}>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#fdfbf7]/60">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-6 font-serif text-4xl md:text-5xl tracking-tight leading-none">
                  {s.value}
                </p>
                <p className="mt-4 font-sans text-sm text-[#fdfbf7]/70">
                  {s.label}
                  {s.note && (
                    <span className="block mt-1 font-mono text-[10px] tracking-[0.18em] uppercase text-[#fdfbf7]/40">
                      {s.note}
                    </span>
                  )}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SIGNATURE STORIES */}
      <Section id="stories">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-16 md:mb-24">
          <Reveal>
            <div>
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#1a1a1a]">
                Chapter 02 — Signature stories
              </p>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#1a1a1a]">
                Quiet lives. Loud echoes.
              </h2>
            </div>
          </Reveal>
          <Reveal>
            <div className="max-w-sm space-y-5">
              <p className="font-sans text-[17px] text-[#4a4a4a] leading-relaxed">
                A handful of recent pieces — picked up by national press,
                shared millions of times, and most importantly, followed by
                action on the ground.
              </p>
              <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#1a1a1a]">
                For more stories, follow us on
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 font-sans text-[15px]">
                <a
                  href="https://www.instagram.com/storiesbyaradhana/"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="stories-sidebar-instagram"
                  className="sba-link-arrow text-[#1a1a1a] hover:text-[#2d4a22] transition-colors"
                >
                  Instagram <span className="arrow">↗</span>
                </a>
                <a
                  href="https://www.facebook.com/storiesbyaradhana"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="stories-sidebar-facebook"
                  className="sba-link-arrow text-[#1a1a1a] hover:text-[#2d4a22] transition-colors"
                >
                  Facebook <span className="arrow">↗</span>
                </a>
                <a
                  href="https://www.youtube.com/@storiesbyaradhanaa"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="stories-sidebar-youtube"
                  className="sba-link-arrow text-[#1a1a1a] hover:text-[#2d4a22] transition-colors"
                >
                  YouTube <span className="arrow">↗</span>
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          {STORIES.map((s, i) => (
            <Reveal
              key={s.title}
              className={
                i === 0
                  ? "md:col-span-7"
                  : i === 1
                    ? "md:col-span-5 md:mt-24"
                    : "md:col-span-8 md:col-start-3 md:-mt-8"
              }
            >
              <article
                data-testid={`story-card-${i + 1}`}
                className="group"
              >
                {/* Image + title link to YouTube when available */}
                {s.link ? (
                  <a
                    href={s.link}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`story-link-${i + 1}`}
                    className="block"
                  >
                    <div
                      className="overflow-hidden max-w-[440px]"
                      style={{ aspectRatio: s.aspect || "16 / 11" }}
                    >
                      <img
                        src={s.img}
                        alt={s.title}
                        loading="lazy"
                        decoding="async"
                        style={{ objectPosition: s.imgPos || "50% 50%" }}
                        className={`w-full h-full object-cover ${s.color ? "" : "grayscale"} transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]`}
                      />
                    </div>
                    <p className="mt-6 font-mono text-[11px] tracking-[0.22em] uppercase text-[#4a4a4a]">
                      {s.kicker}
                    </p>
                    <h3 className="mt-3 font-serif text-2xl md:text-3xl leading-snug tracking-tight text-[#1a1a1a] max-w-xl group-hover:underline underline-offset-4 decoration-[#1a1a1a]/30">
                      {s.title}
                    </h3>
                  </a>
                ) : (
                  <>
                    <div
                      className="overflow-hidden max-w-[440px]"
                      style={{ aspectRatio: s.aspect || "16 / 11" }}
                    >
                      <img
                        src={s.img}
                        alt={s.title}
                        loading="lazy"
                        decoding="async"
                        style={{ objectPosition: s.imgPos || "50% 50%" }}
                        className={`w-full h-full object-cover ${s.color ? "" : "grayscale"} transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]`}
                      />
                    </div>
                    <p className="mt-6 font-mono text-[11px] tracking-[0.22em] uppercase text-[#4a4a4a]">
                      {s.kicker}
                    </p>
                    <h3 className="mt-3 font-serif text-2xl md:text-3xl leading-snug tracking-tight text-[#1a1a1a] max-w-xl">
                      {s.title}
                    </h3>
                  </>
                )}

                {/* Views */}
                <p className="mt-3 font-mono text-[11px] tracking-[0.22em] uppercase text-[#2d4a22]">
                  {s.views}
                </p>

                {/* Watch links — labeled row */}
                {((s.watch && s.watch.length > 0) || s.link) && (
                  <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1a1a1a]">
                      Watch
                    </span>
                    {s.watch && s.watch.length > 0
                      ? s.watch.map((w, idx) => (
                          <span
                            key={w.url}
                            className="inline-flex items-baseline"
                          >
                            <a
                              href={w.url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`story-${i + 1}-watch-${idx + 1}`}
                              className="font-sans text-sm text-[#1a1a1a] underline underline-offset-4 decoration-[#1a1a1a]/30 hover:decoration-[#1a1a1a]"
                            >
                              {w.label}
                              <span className="text-[#4a4a4a]">
                                {" "}
                                ({w.platform})
                              </span>
                            </a>
                            {idx < s.watch.length - 1 && (
                              <span className="ml-3 text-[#1a1a1a]/30">·</span>
                            )}
                          </span>
                        ))
                      : (
                          <a
                            href={s.link}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`story-watch-${i + 1}`}
                            className="font-sans text-sm text-[#1a1a1a] underline underline-offset-4 decoration-[#1a1a1a]/30 hover:decoration-[#1a1a1a]"
                          >
                            On YouTube
                          </a>
                        )}
                  </div>
                )}

                {/* Featured on — labeled row */}
                {s.features && s.features.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1a1a1a]">
                      Featured on
                    </span>
                    {s.features.map((f, idx) => (
                      <span key={f.name} className="inline-flex items-baseline">
                        <a
                          href={f.url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`story-${i + 1}-feature-${f.name
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")}`}
                          className="font-sans text-sm text-[#1a1a1a] underline underline-offset-4 decoration-[#1a1a1a]/30 hover:decoration-[#1a1a1a]"
                        >
                          {f.name}
                        </a>
                        {idx < s.features.length - 1 && (
                          <span className="ml-3 text-[#1a1a1a]/30">·</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* FOUNDATION TEASER */}
      <section
        id="foundation-teaser"
        data-testid="foundation-teaser"
        className="bg-[#2d4a22] text-[#fdfbf7]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative group">
              <div className="absolute -top-5 -left-5 w-24 h-24 bg-[#f9d94b] -z-0 transition-transform duration-700 ease-out group-hover:-translate-x-1 group-hover:-translate-y-1" />
              <div className="relative z-10 w-full aspect-[4/3] overflow-hidden">
                <img
                  src={FOUNDATION_IMG}
                  alt="Artisans at work — Stories by Aradhana Foundation for Change"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                />
              </div>
              <p className="relative z-10 mt-4 font-mono text-[11px] tracking-[0.25em] uppercase text-[#f9d94b]">
                Foundation for Change — On-ground, West Bengal
              </p>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#fdfbf7]/70">
              Foundation for Change
            </p>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.03] tracking-tight">
              When a story isn’t enough, we stay for the rebuilding.
            </h2>
            <p className="mt-8 font-sans text-[17px] leading-relaxed text-[#fdfbf7]/80 max-w-xl">
              The Stories by Aradhana Foundation for Change is our on-ground
              extension — building homes and schools, providing financial
              assistance and supporting livelihoods, and standing up for
              animal welfare across India.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Link
                to="/foundation"
                data-testid="foundation-teaser-cta"
                className="sba-link-arrow font-sans text-sm tracking-[0.15em] uppercase text-[#fdfbf7]/90"
              >
                Visit the foundation
                <span className="arrow">
                  <ArrowRight size={16} strokeWidth={1.4} />
                </span>
              </Link>
            </div>

            {/* FCC Teaser — the Circle invitation */}
            <div className="mt-12 pt-10 border-t border-[#fdfbf7]/15">
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#f9d94b]">
                The Foundation for Change Circle
              </p>
              <p className="mt-5 font-serif italic text-2xl md:text-3xl leading-snug text-[#fdfbf7]">
                Some stories don&rsquo;t end when the video does.
              </p>
              <p className="mt-4 font-sans text-[16px] leading-relaxed text-[#fdfbf7]/80 max-w-lg">
                Become a monthly donor with the Foundation for Change Circle —
                a small, close-knit community sustaining continued care for the
                people whose stories we&rsquo;ve shared.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-6">
                <Link
                  to="/foundation#donate"
                  data-testid="foundation-teaser-join-circle"
                  className="inline-flex items-center gap-3 bg-[#f9d94b] text-[#1a1a1a] px-8 py-4 text-sm font-sans tracking-[0.15em] uppercase hover:bg-[#fde277] transition-colors"
                >
                  Join the Circle
                  <ArrowUpRight size={16} strokeWidth={1.4} />
                </Link>
                <Link
                  to="/how-your-donation-is-used"
                  data-testid="foundation-teaser-transparency"
                  className="sba-link-arrow font-sans text-sm tracking-[0.15em] uppercase text-[#fdfbf7]/90"
                >
                  Where your contribution goes
                  <span className="arrow">
                    <ArrowRight size={16} strokeWidth={1.4} />
                  </span>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRESS MARQUEE */}
      <div id="press">
        <Marquee items={PRESS} slow testId="press-marquee" />
      </div>

      {/* BRANDS GRID */}
      <Section id="brands">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#1a1a1a]">
              Chapter 03 — Brand work
            </p>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#1a1a1a]">
              Told well. Told true.
            </h2>
            <p className="mt-6 font-sans text-[17px] leading-relaxed text-[#4a4a4a] max-w-sm">
              From homegrown brands to leading corporations, we produce films
              — writing, shooting and publishing narratives that fit the
              platform without losing the heart.
            </p>
          </Reveal>
          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-y-10 gap-x-8 border-t border-black/10 pt-10">
            {BRANDS.map((b) => (
              <a
                key={b.name}
                href={b.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`brand-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group inline-flex items-baseline gap-2 font-serif italic text-xl md:text-2xl text-[#1a1a1a]/75 border-b border-black/5 pb-4 hover:text-[#1a1a1a] hover:border-[#1a1a1a] transition-colors"
              >
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  {b.name}
                </span>
                <span
                  aria-hidden="true"
                  className="font-sans not-italic text-sm text-[#2d4a22] opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0"
                >
                  ↗
                </span>
              </a>
            ))}
          </div>
        </div>
      </Section>

      {/* COLLABORATE FORM */}
      <section
        id="collaborate"
        data-testid="collaborate-section"
        className="bg-[#f9d94b]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-5">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#1a1a1a]">
              Chapter 04 — Collaborate
            </p>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#1a1a1a]">
              Have a story we should tell?
            </h2>
            <p className="mt-6 font-sans text-[17px] leading-relaxed text-[#1a1a1a]/80 max-w-sm">
              Brand campaigns, human-interest features, documentary work or a
              quiet tip-off — write to us. We read every message.
            </p>
            <p className="mt-10 font-mono text-[11px] tracking-[0.2em] uppercase text-[#1a1a1a]/80">
              hello@storiesbyaradhana.com
            </p>
          </Reveal>

          <div className="lg:col-span-7">
            <CollaborateForm kind="collaborate" />
          </div>
        </div>
      </section>
    </main>
  );
}

export function CollaborateForm({ kind = "collaborate", compact = false }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    brand: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/collaborate`, { ...form, kind });
      toast.success("Thank you — we’ll be in touch shortly.");
      setForm({ name: "", email: "", brand: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid={`collaborate-form-${kind}`}
      className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-6`}
    >
      <input
        type="text"
        placeholder="Your name"
        aria-label="Your name"
        value={form.name}
        onChange={onChange("name")}
        className="sba-input"
        data-testid="collab-input-name"
        required
      />
      <input
        type="email"
        placeholder="Email"
        aria-label="Email"
        value={form.email}
        onChange={onChange("email")}
        className="sba-input"
        data-testid="collab-input-email"
        required
      />
      <input
        type="text"
        placeholder={kind === "foundation" ? "Organization (optional)" : "Brand / Publication (optional)"}
        aria-label="Brand"
        value={form.brand}
        onChange={onChange("brand")}
        className={`sba-input ${compact ? "" : "md:col-span-2"}`}
        data-testid="collab-input-brand"
      />
      <textarea
        placeholder={kind === "foundation" ? "Tell us about the cause or the person you want to support" : "Tell us about the story or campaign"}
        aria-label="Message"
        rows={5}
        value={form.message}
        onChange={onChange("message")}
        className={`sba-input resize-none ${compact ? "" : "md:col-span-2"}`}
        data-testid="collab-input-message"
        required
      />
      <div className={`flex items-center justify-between gap-4 ${compact ? "" : "md:col-span-2"} pt-4`}>
        <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#1a1a1a]/60">
          We typically reply within 48 hours
        </p>
        <button
          type="submit"
          disabled={loading}
          data-testid="collab-submit"
          className="inline-flex items-center gap-3 bg-[#1a1a1a] text-[#fdfbf7] px-8 py-4 text-sm font-sans tracking-[0.15em] uppercase hover:bg-[#2d2d2d] transition-colors disabled:opacity-60"
        >
          {loading ? "Sending…" : "Send message"}
          <ArrowRight size={16} strokeWidth={1.4} />
        </button>
      </div>
    </form>
  );
}
